import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { getCurrentUser } from "@/lib/auth";
import type { User } from "@/lib/auth";
import Navbar from "@/components/shared/layout/navbar";
import DashboardContainer from "@/components/shared/layout/DashboardContainer";
import PageHeader from "@/components/shared/layout/PageHeader";
import ProjectGrid from "@/components/shared/dashboard/ProjectGrid";
import ProjectCard from "@/components/shared/dashboard/ProjectCard";
import ProjectCardSkeleton from "@/components/shared/dashboard/ProjectCardSkeleton";
import EmptyState from "@/components/shared/ui/EmptyState";
import Button from "@/components/shared/ui/Button";

interface Project {
  id: string;
  name: string;
  tagline?: string;
  thumbnail?: string;
  project_status?: string;
  completion_percentage?: number;
  access_level: "visible" | "locked" | "hidden";
  permission_level?: "advisor" | "partner" | "client";
  resource_count?: number;
  display_order?: number;
  // Visibility flags from user_project_access_client
  show_documents?: boolean;
  show_project_notes?: boolean;
  show_directory?: boolean;
  show_metrics?: boolean;
}

interface ProjectThread {
  id: string;
  name: string;
  description?: string;
  display_order: number;
}

interface ThreadWithProjects {
  thread: ProjectThread;
  projects: Project[];
}

export default function ClientDashboard() {
  const [user, setUser] = useState<User | null>(null);
  const [accessLevel, setAccessLevel] = useState<
    "advisor" | "partner" | "client" | null
  >(null);
  const [threadedProjects, setThreadedProjects] = useState<
    ThreadWithProjects[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Get current user
      const currentUser = await getCurrentUser();
      if (!currentUser) {
        setError("User not found");
        setLoading(false);
        return;
      }
      setUser(currentUser);

      // Fetch access level from users table
      const { data: userData, error: userError } = await supabase
        .from("users")
        .select("access_level")
        .eq("id", currentUser.id)
        .single();

      if (userError) throw userError;
      setAccessLevel(
        userData?.access_level as "advisor" | "partner" | "client" | null
      );

      // Fetch access records for this client
      const { data: accessData, error: accessError } = await supabase
        .from("user_project_access_client")
        .select("*")
        .eq("user_id", currentUser.id)
        .eq("access_level", "visible");

      if (accessError) throw accessError;

      if (!accessData || accessData.length === 0) {
        setThreadedProjects([]);
        setLoading(false);
        return;
      }

      // Fetch actual projects separately
      const projectIds = accessData.map((a: any) => a.project_id);
      const { data: projectsData, error: projectsError } = await supabase
        .from("projects")
        .select(
          `
          id,
          name,
          tagline,
          thumbnail,
          thread_id,
          display_order,
          project_threads (
            id,
            name,
            description,
            display_order
          )
        `
        )
        .in("id", projectIds);

      if (projectsError) throw projectsError;

      if (!projectsData || projectsData.length === 0) {
        setThreadedProjects([]);
        setLoading(false);
        return;
      }

      // Combine access data with project data
      const transformedProjects = (await Promise.all(
        projectsData.map(async (project: any) => {
          // Find the access record for this project
          const accessRecord = accessData.find(
            (a: any) => a.project_id === project.id
          );

          // Get client metrics for this project
          const { data: metricsData } = await supabase
            .from("project_metrics_client")
            .select("*")
            .eq("project_id", project.id)
            .maybeSingle();

          // Count total resources (documents only for now)
          const { count: docsCount } = await supabase
            .from("documents")
            .select("id", { count: "exact", head: true })
            .eq("project_id", project.id);

          const totalResources = docsCount || 0;

          return {
            id: project.id,
            name: project.name,
            tagline: project.tagline ?? "Project Details",
            thumbnail: project.thumbnail ?? undefined,
            project_status: metricsData?.metric_1 ?? undefined,
            completion_percentage:
              metricsData?.metric_2 !== undefined
                ? parseInt(metricsData.metric_2)
                : undefined,
            access_level: accessRecord?.access_level || "visible",
            permission_level: accessRecord?.permission_level,
            resource_count: totalResources,
            display_order: project.display_order ?? 0,
            thread: project.project_threads,
            // Pass visibility flags from user_project_access_client
            show_documents: accessRecord?.show_documents ?? true,
            show_project_notes: accessRecord?.show_project_notes ?? true,
            show_directory: accessRecord?.show_directory ?? true,
            show_metrics: accessRecord?.show_metrics ?? true,
          };
        })
      )) as (Project & { thread: any })[];

      // Group projects by thread
      const projectsByThread = new Map<string, ThreadWithProjects>();

      transformedProjects.forEach((project) => {
        const threadId = project.thread?.id || "no-thread";

        if (!projectsByThread.has(threadId)) {
          projectsByThread.set(threadId, {
            thread: project.thread || {
              id: "no-thread",
              name: "Active Projects",
              description: "Current client projects",
              display_order: 0,
            },
            projects: [],
          });
        }

        const { thread, ...projectWithoutThread } = project;
        projectsByThread.get(threadId)!.projects.push(projectWithoutThread);
      });

      // Sort threads by display_order
      const sortedThreads = Array.from(projectsByThread.values()).sort(
        (a, b) => a.thread.display_order - b.thread.display_order
      );

      // Sort projects within each thread by display_order
      sortedThreads.forEach((threadData) => {
        threadData.projects.sort(
          (a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)
        );
      });

      setThreadedProjects(sortedThreads);
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      setError("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleRequestAccess = () => {
    window.location.href =
      "mailto:alex@tcglventures.com?subject=Project Access Request&body=I would like to request access to additional projects.";
  };

  const totalProjects = threadedProjects.reduce(
    (sum, thread) => sum + thread.projects.length,
    0
  );

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-black">
        {user && <Navbar user={user} />}
        <DashboardContainer>
          <PageHeader
            title="Your Projects"
            description="Loading your projects..."
          />
          <ProjectGrid>
            <ProjectCardSkeleton />
            <ProjectCardSkeleton />
            <ProjectCardSkeleton />
          </ProjectGrid>
        </DashboardContainer>
      </div>
    );
  }

  // Error state
  if (error || !user) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Error</h1>
          <p className="text-gray-400">{error || "Failed to load user data"}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <Navbar user={user} />

      <DashboardContainer>
        {/* Projects Section */}
        <PageHeader
          title="Your Projects"
          description="Track deliverables and milestones for your consultancy projects"
        />

        {/* Empty State */}
        {totalProjects === 0 && (
          <EmptyState
            title="No Projects Yet"
            description="Projects will appear here once you've been granted access. Contact us to discuss your project needs."
            action={
              <Button variant="secondary" onClick={handleRequestAccess}>
                Request Access
              </Button>
            }
          />
        )}

        {/* Threaded Projects - Each thread gets its own horizontal row */}
        {threadedProjects.map((threadData) => (
          <div key={threadData.thread.id} className="mb-12">
            {/* Thread Header */}
            <div className="mb-4">
              <h2 className="text-2xl font-black text-white uppercase tracking-wide">
                {threadData.thread.name}
              </h2>
              {threadData.thread.description && (
                <p className="text-sm text-gray-400 mt-1">
                  {threadData.thread.description}
                </p>
              )}
            </div>

            {/* Thread Projects Grid */}
            <ProjectGrid>
              {threadData.projects.map((project) => (
                <ProjectCard
                  key={project.id}
                  project={{
                    ...project,
                    user_type: "client",
                    resource_count: project.resource_count,
                    show_action_buttons: true,
                  }}
                  userType="client"
                  currentUserType={
                    project.permission_level ?? accessLevel ?? undefined
                  }
                />
              ))}
            </ProjectGrid>
          </div>
        ))}
      </DashboardContainer>
    </div>
  );
}
